/*
    Minimal page for viewing and printing reports.
*/

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button } from 'antd';
import { CaretDownFilled, CaretUpFilled, PrinterOutlined } from '@ant-design/icons';
import { PollingContext, useDataActions } from './item-content/polling-context';
import ItemContent from './item-content/item-content';
import ItemContentContext from './item-content/item-content-context';
import axios from 'axios';
import { useLocation, useParams } from 'react-router-dom';
import styles from '../style/report-page.module.css';
import { useSelector } from 'react-redux';
import ReactDOM from 'react-dom';
import { getScrollIndicators } from '../utils/scroll-indicators';

const ReportPageContent = () => {
  const { recordIDs, reportID } = useParams();
  const token = useSelector((state) => state.token);
  const location = useLocation();
  const normsGender = new URLSearchParams(location.search).get('normsGender') || undefined;

  const printRef = useRef(() => {});
  const contentRef = useRef(null);

  const [data, setData] = useState({});
  const [document, setDocument] = useState([]);
  const [charge, setCharge] = useState(null); // { charged, creditsRemaining } once a report is generated
  const [error, setError] = useState(null); // why the report could not be generated
  const [scrollIndicators, setScrollIndicators] = useState({ showAbove: false, showBelow: false });

  const updateScrollIndicators = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    setScrollIndicators(
      getScrollIndicators({
        scrollTop: el.scrollTop,
        clientHeight: el.clientHeight,
        scrollHeight: el.scrollHeight
      })
    );
  }, []);

  const { updateData } = useDataActions();

  const getReport = useCallback(async () => {
    if (!recordIDs || !reportID) return;

    const recordIDList = recordIDs.split(',').filter((id) => id !== '');

    const data = {
      recordIDs: recordIDList,
      reportID: reportID,
      normsGender: normsGender
    };

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Token ' + token
      }
    };

    // The API explains a refusal in its body — e.g. 422 for a questionnaire
    // record with unanswered items (PIN-221) — so show that instead of a blank page.
    setError(null);
    let response;
    try {
      response = await axios.post(process.env.REACT_APP_API_URL + '/report/generate', data, config);
    } catch (err) {
      console.error('Report generation failed:', err);
      setError(
        err?.response?.data?.message || err?.message || 'The report could not be generated.'
      );
      return;
    }

    ReactDOM.unstable_batchedUpdates(() => {
      setDocument(response.data.document);
      setData(response.data.data);
      setCharge({
        charged: response.data.charged,
        creditsRemaining: response.data.creditsRemaining
      });
    });
  }, [token, recordIDs, reportID, normsGender]);

  useEffect(() => {
    if (!recordIDs || !reportID || !token) return;

    getReport();
  }, [getReport, recordIDs, reportID, token]);

  useEffect(() => {
    updateData(data);
  }, [data, updateData]);

  // Recompute whenever the scroll viewport or the rendered report content's
  // own (unconstrained, content-driven) size changes — covers the initial
  // async document load, window resizes, and any reflow in between. jsdom
  // has no ResizeObserver, so this degrades to a single initial check there.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    updateScrollIndicators();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(updateScrollIndicators);
    observer.observe(el);
    const reportContainer = el.querySelector('[data-testid="report-container"]');
    if (reportContainer) observer.observe(reportContainer);

    return () => observer.disconnect();
  }, [updateScrollIndicators]);

  return (
    <div className={styles['container']}>
      <div
        className={styles['content'] + ' hide-scrollbars'}
        ref={contentRef}
        onScroll={updateScrollIndicators}
      >
        {error && (
          <div data-testid="report-error">
            <Alert
              type="error"
              showIcon
              message="Report could not be generated"
              description={error}
            />
          </div>
        )}
        <ItemContentContext readOnly value={document}>
          <ItemContent
            layout="report"
            printRef={printRef}
            printDocumentTitle={'Schoolhouse report'} // TODO: make this ~'testName, recordLabel, examineeName' etc.
          />
        </ItemContentContext>
      </div>
      <CaretUpFilled
        className={styles['scroll-indicator'] + ' ' + styles['scroll-indicator-above']}
        data-testid="scroll-indicator-above"
        data-visible={scrollIndicators.showAbove ? 'true' : 'false'}
      />
      <div className={styles['footer']}>
        <CaretDownFilled
          className={styles['scroll-indicator'] + ' ' + styles['scroll-indicator-below']}
          data-testid="scroll-indicator-below"
          data-visible={scrollIndicators.showBelow ? 'true' : 'false'}
        />
        {charge &&
          (charge.charged ? (
            <span className={styles['charge-status']}>
              1 credit used — {charge.creditsRemaining} credits remaining
            </span>
          ) : (
            <span className={styles['charge-status']}>Already unlocked — no credit charged</span>
          ))}
        <Button type="primary" onClick={printRef.current} icon={<PrinterOutlined />}>
          Print
        </Button>
      </div>
    </div>
  );
};

// a wraper for applying data context
const ReportPage = () => {
  return (
    <PollingContext offline>
      <ReportPageContent />
    </PollingContext>
  );
};

export default ReportPage;
